/**
 * OrgLibrary — shared knowledge-base "Library" for an organization.
 *
 * One source of truth for the four KB sections used by BOTH:
 *   - Consultant Client Profile (orgId = the client's org)
 *   - Contractor Workspace Settings (orgId = the contractor's own org)
 *
 * Sections: Certifications, Past Performance, Key Personnel, Winning Proposals.
 * Reuses the existing CertificationFormModal / PastPerformanceFormModal /
 * KeyPersonnelFormModal and WinningProposalsCard. All org-scoped via
 * organization_id, which a contractor can self-edit.
 *
 * Props:
 *   orgId        — organization to manage
 *   allowPromote — show "promote to global library" (consultant only). Default false.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plus, Pencil, Trash2, Award, Briefcase, UserRound, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  CertificationFormModal,
  PastPerformanceFormModal,
  KeyPersonnelFormModal,
} from '../kb/ClientProfileForms';
import WinningProposalsCard from '../kb/WinningProposalsCard';
import PromoteToLibraryButton from '../consultant/PromoteToLibraryButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) =>
  v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : null;

export default function OrgLibrary({ orgId, allowPromote = false }) {
  const { authToken } = useAuth();
  const auth = { Authorization: `Bearer ${authToken}` };

  const [certs, setCerts] = useState(null);
  const [pastPerf, setPastPerf] = useState(null);
  const [keyPpl, setKeyPpl] = useState(null);

  // modal state: { kind: 'cert'|'pp'|'kp', initial: {...}|null }
  const [modal, setModal] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!orgId || !authToken) return;
    try {
      const [cRes, pRes, kRes] = await Promise.all([
        fetch(`${API_URL}/api/knowledge-base/certifications?organization_id=${orgId}`, { headers: auth }),
        fetch(`${API_URL}/api/knowledge-base/past-performances?organization_id=${orgId}`, { headers: auth }),
        fetch(`${API_URL}/api/knowledge-base/key-personnel?organization_id=${orgId}`, { headers: auth }),
      ]);
      setCerts(cRes.ok ? (await cRes.json()).items || [] : []);
      setPastPerf(pRes.ok ? (await pRes.json()).items || [] : []);
      setKeyPpl(kRes.ok ? (await kRes.json()).items || [] : []);
    } catch (e) {
      setCerts([]); setPastPerf([]); setKeyPpl([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, authToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const del = async (domain, id, label) => {
    if (!window.confirm(`Delete this ${label}?\n\nThis also removes any attachments.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/knowledge-base/${domain}/${id}`, { method: 'DELETE', headers: auth });
      if (!res.ok) throw new Error('Delete failed');
      toast.success(`${label} removed`);
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const SectionHeader = ({ icon: Icon, title, count, onAdd }) => (
    <div className="flex items-center justify-between">
      <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Icon className="w-5 h-5 text-slate-500" /> {title}
        <Badge variant="secondary" className="ml-1 text-xs">{count}</Badge>
      </CardTitle>
      <Button size="sm" variant="outline" onClick={onAdd} className="gap-1.5 h-8 text-xs">
        <Plus className="w-3.5 h-3.5" /> Add
      </Button>
    </div>
  );

  const RowActions = ({ onEdit, onDelete, promote }) => (
    <div className="flex items-center gap-1 flex-shrink-0">
      {promote}
      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={onEdit}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={onDelete}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );

  const loading = certs === null || pastPerf === null || keyPpl === null;
  if (loading) {
    return <Card><CardContent className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></CardContent></Card>;
  }

  return (
    <div className="space-y-6" data-testid="org-library">
      {/* ── Certifications ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <SectionHeader icon={Award} title="Certifications" count={certs.length}
            onAdd={() => setModal({ kind: 'cert', initial: null })} />
        </CardHeader>
        <CardContent className="space-y-2">
          {certs.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-2">No certifications yet.</p>
          ) : certs.map((c) => (
            <div key={c.id} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50">
              <Award className={cn('w-4 h-4 flex-shrink-0',
                c.status === 'ACTIVE' ? 'text-emerald-600' : c.status === 'EXPIRED' ? 'text-red-600' : 'text-amber-600')} />
              <span className="flex-1 text-sm font-medium text-slate-900 truncate">{c.name}</span>
              {c.issuing_body && <span className="text-xs text-slate-400 truncate hidden sm:block">{c.issuing_body}</span>}
              <Badge variant="outline" className={cn('text-xs flex-shrink-0',
                c.status === 'ACTIVE' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : c.status === 'EXPIRED' ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700')}>
                {c.status || 'PENDING'}
              </Badge>
              <RowActions
                promote={allowPromote ? <PromoteToLibraryButton kind="certification" recordId={c.id} defaultTitle={c.name} /> : null}
                onEdit={() => setModal({ kind: 'cert', initial: c })}
                onDelete={() => del('certifications', c.id, 'certification')} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Past Performance ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <SectionHeader icon={Briefcase} title="Past Performance" count={pastPerf.length}
            onAdd={() => setModal({ kind: 'pp', initial: null })} />
        </CardHeader>
        <CardContent className="space-y-2">
          {pastPerf.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-2">No past performance records yet.</p>
          ) : pastPerf.map((p) => (
            <div key={p.id} className="flex items-start gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50">
              <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{p.contract_title || 'Untitled contract'}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[p.client_agency, fmtMoney(p.contract_value), p.cpars_rating].filter(Boolean).join(' · ')}
                </p>
              </div>
              <RowActions
                promote={allowPromote ? <PromoteToLibraryButton kind="past_performance" recordId={p.id} defaultTitle={p.contract_title} /> : null}
                onEdit={() => setModal({ kind: 'pp', initial: p })}
                onDelete={() => del('past-performances', p.id, 'past performance')} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Key Personnel ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <SectionHeader icon={UserRound} title="Key Personnel" count={keyPpl.length}
            onAdd={() => setModal({ kind: 'kp', initial: null })} />
        </CardHeader>
        <CardContent className="space-y-2">
          {keyPpl.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-2">No key personnel yet.</p>
          ) : keyPpl.map((k) => (
            <div key={k.id} className="flex items-center gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50">
              <UserRound className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="flex-1 text-sm font-medium text-slate-900 truncate">
                {[k.first_name, k.last_name].filter(Boolean).join(' ') || k.person_name || 'Unnamed'}
              </span>
              {(k.job_title || k.role_title) && (
                <span className="text-xs text-slate-500 truncate hidden sm:block">{k.job_title || k.role_title}</span>
              )}
              {k.clearance_level && <Badge variant="outline" className="text-xs flex-shrink-0">{k.clearance_level}</Badge>}
              <RowActions
                promote={allowPromote ? <PromoteToLibraryButton kind="key_personnel" recordId={k.id} defaultTitle={`${k.first_name || ''} ${k.last_name || ''}`.trim()} /> : null}
                onEdit={() => setModal({ kind: 'kp', initial: k })}
                onDelete={() => del('key-personnel', k.id, 'key personnel')} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Winning Proposals ── (self-contained card) */}
      <WinningProposalsCard organizationId={orgId} />

      {/* ── Modals ── */}
      <CertificationFormModal
        open={modal?.kind === 'cert'} onOpenChange={(v) => !v && setModal(null)}
        organizationId={orgId} initial={modal?.kind === 'cert' ? modal.initial : null}
        onSaved={() => { setModal(null); fetchAll(); }} />
      <PastPerformanceFormModal
        open={modal?.kind === 'pp'} onOpenChange={(v) => !v && setModal(null)}
        organizationId={orgId} initial={modal?.kind === 'pp' ? modal.initial : null}
        onSaved={() => { setModal(null); fetchAll(); }} />
      <KeyPersonnelFormModal
        open={modal?.kind === 'kp'} onOpenChange={(v) => !v && setModal(null)}
        organizationId={orgId} initial={modal?.kind === 'kp' ? modal.initial : null}
        onSaved={() => { setModal(null); fetchAll(); }} />
    </div>
  );
}
